import { API_BASE, getApiHeaders } from './apiClient';

export interface ChatMessage {
  role:        'user' | 'assistant';
  content:     string;
  timestamp:   string;
  authorEmail?: string | null;
}

// Detect dev vs prod so error messages aren't misleading. The old copy
// always said "Make sure it is running on port 3004" which is nonsense
// in prod — Yash panicked during a demo thinking the app was broken.
const IS_DEV = /localhost|127\.0\.0\.1/.test(API_BASE);

/** One-shot fetch. Chat turns can take 10-20s because Bedrock tool loops
 *  run synchronously; the browser has no default timeout but API Gateway
 *  hard-limits at 29s. On network / timeout errors we retry up to 2 times
 *  after a short pause so a single cold-Lambda blip doesn't fail a demo.
 *  Errors carry an `isPending` flag so callers can show a softer "still
 *  processing — the reply will appear when ready" message instead of an
 *  alarming red toast (the 5-second poll picks it up either way). */
export class ChatPendingError extends Error {
  isPending = true;
  constructor(message: string) {
    super(message);
    this.name = 'ChatPendingError';
  }
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers = { ...getApiHeaders(), ...options?.headers };
  const doFetch = () => fetch(url, { ...options, headers });

  let res: Response | null = null;
  let lastErr: any = null;
  // Single attempt for chat sends — retrying spawns DUPLICATE Bedrock
  // invocations on the server (the original Lambda is still running
  // when the browser aborts on long agent-tool-loops, retry creates a
  // second concurrent agent call, both eventually save independent
  // replies). Yash hit this: one update message produced 3+ stacked
  // agent replies. The 5s poller already covers transient drops.
  try {
    res = await doFetch();
  } catch (err) {
    lastErr = err;
  }
  if (!res) {
    // All 3 attempts failed at the network layer. The agent send may
    // still be in flight server-side (Lambda was processing when the
    // browser dropped). Throw a "pending" error so the chat UI can show
    // a soft inline note instead of a scary toast — the 5-second poller
    // will pick up the agent's reply when it lands.
    throw new ChatPendingError(IS_DEV
      ? 'Cannot reach the CIMA backend. Make sure it is running on port 3004.'
      : 'Slow network — the agent is still processing. Your reply will appear here when it\u2019s ready.'
    );
  }

  // Gateway timed out mid-Bedrock call (504) — agent is still running
  // on the Lambda side, the reply will land via the poller. Same soft
  // pending error so we don't yell at the user.
  if (res.status === 504 || res.status === 502) {
    throw new ChatPendingError(
      'The agent is taking a bit longer than usual — the reply will appear here when it\u2019s ready.',
    );
  }

  const text = await res.text();
  let data: any;
  try { data = JSON.parse(text); } catch {
    throw new Error(`Unexpected server response (${res.status})`);
  }
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data as T;
}

export async function openChat(
  incidentId: string,
): Promise<{ messages: ChatMessage[]; readyForSitrep: boolean }> {
  return apiFetch(`/cima/incidents/${incidentId}/chat/open`, { method: 'POST' });
}

export async function sendMessage(
  incidentId: string,
  message: string,
): Promise<{ message: ChatMessage; readyForSitrep: boolean; assessedSeverity?: string; incidentClosed?: boolean }> {
  return apiFetch(`/cima/incidents/${incidentId}/chat`, {
    method:  'POST',
    body:    JSON.stringify({ message }),
  });
}
